import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface AlertProps {
  title?: string;
  children: React.ReactNode;
  variant?: 'info' | 'success' | 'warning' | 'error';
  icon?: React.ElementType;
  onClose?: () => void;
  className?: string;
}

function Alert({ title, children, variant = 'info', icon: Icon, onClose, className }: AlertProps) {
  // Ensure variant is one of the allowed values
  const safeVariant: 'info' | 'success' | 'warning' | 'error' = [
    'info',
    'success',
    'warning',
    'error',
  ].includes(variant)
    ? variant
    : 'info';

  const variants = {
    info: {
      container: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
      defaultIcon: Info,
      iconClass: 'text-blue-500 dark:text-blue-400',
      title: 'text-blue-800 dark:text-blue-300',
      content: 'text-blue-700 dark:text-blue-200',
    },
    success: {
      container: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
      defaultIcon: CheckCircle,
      iconClass: 'text-green-500 dark:text-green-400',
      title: 'text-green-800 dark:text-green-300',
      content: 'text-green-700 dark:text-green-200',
    },
    warning: {
      container: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
      defaultIcon: AlertTriangle,
      iconClass: 'text-yellow-500 dark:text-yellow-400',
      title: 'text-yellow-800 dark:text-yellow-300',
      content: 'text-yellow-700 dark:text-yellow-200',
    },
    error: {
      container: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800',
      defaultIcon: AlertCircle,
      iconClass: 'text-red-500 dark:text-red-400',
      title: 'text-red-800 dark:text-red-300',
      content: 'text-red-700 dark:text-red-200',
    },
  };

  const variantStyles = variants[safeVariant];
  const IconComponent = Icon || variantStyles.defaultIcon;

  return (
    <div
      className={cn('p-4 border rounded-md relative', variantStyles.container, className)}
      role="alert"
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <IconComponent className={cn('h-5 w-5', variantStyles.iconClass)} />
        </div>
        <div className="ml-3 flex-1">
          {title && <h3 className={cn('text-sm font-medium', variantStyles.title)}>{title}</h3>}
          <div className={cn('text-sm', variantStyles.content)}>{children}</div>
        </div>
        {onClose && (
          <button
            type="button"
            className="ml-auto -mx-1.5 -my-1.5 rounded-md p-1.5 inline-flex text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent focus:ring-primary-500"
            onClick={onClose}
          >
            <span className="sr-only">Fechar</span>
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default Alert;
